import React, { ReactElement } from "react"
import { TableCell, TableRow, Typography } from "@mui/material"
import { useStyles } from "../styles"
import { Columns } from "../types"
import Checkbox from "../../Checkbox"

interface Props<T> {
  record: T
  selectedRows: string[]
  disableSelect?: boolean
  displayedProperties: string[]
  allDisplayedProperties?: string[]
  headerColumns: Columns[]
  disableCheckbox?: boolean
  onSelectRow: (id: string) => void
  onClick?: (id: string) => void
  getRowAction?: (id: string) => ReactElement
  children?: (prop: string, record: T) => React.ReactNode
}

const TableRowComponent = <T extends { id: string }>({
  record,
  selectedRows,
  disableSelect = false,
  displayedProperties,
  allDisplayedProperties,
  headerColumns,
  disableCheckbox = false,
  onSelectRow,
  onClick,
  getRowAction,
  children
}: React.PropsWithChildren<Props<T>>) => {
  const { classes, cx } = useStyles()

  const isInteractive = !!onClick || !disableCheckbox

  const handleRowClick = () => {
    onClick?.(record.id)
  }

  const handleRowSelect = () => {
    onSelectRow?.(record.id)
  }

  return (
    <TableRow
      id={`${record.id}`}
      tabIndex={isInteractive ? 0 : -1}
      className={cx(classes.row, {
        [classes.selectedRow]: selectedRows?.includes(record.id)
      })}
      data-automation-id="data-table-row"
    >
      {!disableCheckbox && (
        <TableCell
          className={cx(classes.tableCell, classes.bodyTableCell)}
          data-automation-id="data-table-checkbox-cell"
        >
          <Checkbox
            checked={selectedRows?.includes(record.id)}
            onChange={handleRowSelect}
            disabled={disableSelect}
            className={classes.checkbox}
            data-automation-id="data-table-checkbox"
          />
        </TableCell>
      )}
      {headerColumns
        .filter(({ propertyId }) => displayedProperties.includes(propertyId))
        .map(({ propertyId }) => (
          <TableCell
            key={propertyId}
            className={cx(classes.tableCell, classes.bodyTableCell)}
            onClick={onClick ? handleRowClick : undefined}
            data-automation-id="data-table-cell"
          >
            {children?.(propertyId, record) || (
              <Typography variant="body2">
                {record[propertyId] || "- -"}
              </Typography>
            )}
          </TableCell>
        ))}
      {(!!getRowAction || !!allDisplayedProperties) && (
        <TableCell
          className={cx(classes.tableCell, classes.bodyTableCell)}
          data-automation-id="data-table-action-cell"
        >
          {getRowAction?.(record.id)}
        </TableCell>
      )}
    </TableRow>
  )
}

export default TableRowComponent
